import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, saveAuth, clearAuth, getUser, getToken, fmtErr } from "./api";

const AuthCtx = createContext(null);

const REF_KEY = "noi_ref_code";

// Best-effort: if a ?ref=CODE is in the current URL, stash it in
// localStorage so we can attach the referral once the user is authed.
export function captureReferralFromUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const ref = (sp.get("ref") || "").trim().toUpperCase();
    if (ref && ref.length >= 4 && ref.length <= 12) {
      localStorage.setItem(REF_KEY, ref);
    }
  } catch (e) { /* ignore */ }
}

async function _attachReferralIfPending(user) {
  if (!user || user.role !== "patient") return;
  let code = "";
  try { code = (localStorage.getItem(REF_KEY) || "").trim().toUpperCase(); } catch { return; }
  if (!code) return;
  try {
    const { data } = await api.post("/patients/me/referral/attach", { code });
    try { localStorage.removeItem(REF_KEY); } catch (e) { /* ignore */ }
    // Only celebrate on a fresh attach — not on already-attached re-hydrations.
    if (data && !data.already_attached && data.discount_rupees) {
      // Fire GA4 event for the attribution funnel.
      try { window.gtag?.("event", "referral_attached", { code, discount: data.discount_rupees }); } catch (_e) { /* gtag optional */ }
      // Show a delayed toast so it appears after the "Welcome" toast from
      // the auth flow. sonner de-duplicates by content automatically.
      setTimeout(() => {
        toast.success(
          `🎁 ₹${data.discount_rupees} referral credit applied — use code ${data.coupon_code} on your next case`,
          { duration: 8000 },
        );
      }, 900);
    }
  } catch (e) {
    // 400 (already paid / self-refer) → discard the code; 404 (invalid) → discard.
    const status = e?.response?.status;
    if (status === 400 || status === 404) {
      try { localStorage.removeItem(REF_KEY); } catch (er) { /* ignore */ }
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    api.get("/auth/me").then(r => {
      setUser(r.data);
      _attachReferralIfPending(r.data);
    })
      .catch(() => { clearAuth(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    saveAuth(data.token, data.user);
    setUser(data.user);
    _attachReferralIfPending(data.user);
    return data.user;
  };
  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    saveAuth(data.token, data.user);
    setUser(data.user);
    _attachReferralIfPending(data.user);
    return data.user;
  };
  const otpSend = async (phone) => (await api.post("/auth/otp/send", { phone })).data;
  const otpVerify = async (phone, otp, name, age, gender, country) => {
    const { data } = await api.post("/auth/otp/verify", { phone, otp, name, age, gender, country });
    saveAuth(data.token, data.user);
    setUser(data.user);
    _attachReferralIfPending(data.user);
    return data.user;
  };
  const logout = () => { clearAuth(); setUser(null); };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, signup, otpSend, otpVerify, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { fmtErr };
